'use client';

/**
 * ChatThreadCard — registry-driven message thread card.
 *
 * Renders the messages portion of the legacy `<ChatPanel />` as a
 * standalone card consumable by the registry-driven `<ChatSurface />`.
 * Selection-aware via `useChatSurface()` (the surface-level provider
 * that owns the single chat strategy fetch + sibling tab state).
 *
 * Sub-elements:
 *   - sticky header (entity name + count badge)
 *   - per-vendor sibling tabs (only when this is a multi-vendor tender)
 *   - scrollable message list with translation toggle, attachments,
 *     and per-attachment Document Intelligence extract action
 *
 * Mirror of `<DetailsCard />` on the rail / canvas surfaces — same
 * selection-aware-card primitive, different content_key.
 *
 * @see klyde_edge/composition/cards/messaging.py — chat_thread CardSpec
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Frontend / Messaging
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { Globe, MessageSquare, Loader2, Paperclip, FileText, Image, ScanLine } from 'lucide-react';
import { useCardExport } from '@/lib/design/card-export';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { PlatformBadge } from '../details/shared';
import { useExtractFromMessage } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { cn } from '@/lib/utils/cn';
import { useChatSurface, ALL_SIBLINGS } from './chat-surface-context';
import type { ChatMessage } from './chat-surface-context';

// ── Helpers (lifted from legacy ChatPanel) ────────────────────────

function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '  • ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const AVATAR_COLORS = [
  'bg-primary/15 text-primary',
  'bg-success/15 text-success',
  'bg-warning/15 text-warning',
  'bg-info/15 text-info',
  'bg-destructive/15 text-destructive',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function parseAttachments(urls: string[] | null, metadataJson: string | null) {
  if (!urls || urls.length === 0) return [];
  let metadata: Array<{ filename: string; mimetype: string; size: number }> = [];
  if (metadataJson) {
    try { metadata = JSON.parse(metadataJson); } catch { /* fallback */ }
  }
  return urls.map((url, i) => {
    const meta = metadata[i] || { filename: '', mimetype: '', size: 0 };
    return {
      url,
      filename: meta.filename || url.split('/').pop() || 'attachment',
      mimetype: meta.mimetype || 'application/octet-stream',
      size: meta.size || 0,
    };
  });
}

function getFileIcon(mimetype: string) {
  if (mimetype.startsWith('image/')) return Image;
  if (mimetype === 'application/pdf') return FileText;
  return Paperclip;
}

const DI_ELIGIBLE_MIMETYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]);

function formatSize(bytes: number): string {
  if (bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(dateStr: string): string {
  const trimmed = dateStr.trim();
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$|[+-]\d{4}$/.test(trimmed);
  const d = hasTimezone
    ? new Date(trimmed)
    : new Date(trimmed.replace(' ', 'T') + 'Z');
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function TranslatedBody({ msg }: { msg: ChatMessage }) {
  const [showOriginal, setShowOriginal] = useState(false);
  const hasTranslation = !!msg.body_translated;
  const originalBody = stripHtml(msg.body);
  const translatedBody = msg.body_translated ? stripHtml(msg.body_translated) : null;
  const body = hasTranslation && !showOriginal ? translatedBody : originalBody;

  return (
    <>
      {hasTranslation && (
        <button
          onClick={() => setShowOriginal(!showOriginal)}
          className="inline-flex items-center gap-0.5 text-[9px] text-muted-foreground hover:text-foreground transition-colors mb-0.5"
          title={showOriginal ? 'View translation' : 'View original'}
        >
          <Globe className="h-2.5 w-2.5" />
          {showOriginal ? msg.language?.toUpperCase() : msg.translated_to?.toUpperCase()}
        </button>
      )}
      {body && (
        <p className="text-xs text-foreground whitespace-pre-wrap break-words leading-relaxed">
          {body}
        </p>
      )}
    </>
  );
}

interface SiblingTabProps {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}

function SiblingTab({ active, label, count, onClick }: SiblingTabProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors shrink-0',
        active
          ? 'bg-primary text-primary-foreground'
          : 'bg-card text-muted-foreground hover:text-foreground hover:bg-accent border border-border',
      )}
    >
      <span className="truncate max-w-[120px]">{label}</span>
      <span
        className={cn(
          'text-[10px] tabular-nums px-1 rounded',
          active ? 'bg-primary-foreground/20' : 'bg-muted',
        )}
      >
        {count}
      </span>
    </button>
  );
}

// ── ChatThreadCard ────────────────────────────────────────────────

export function ChatThreadCard() {
  const {
    entityType,
    entityId,
    chatData,
    isLoading,
    error,
    allMessages,
    visibleMessages,
    siblings,
    isTender,
    countsBySibling,
    activeSibling,
    setActiveSibling,
  } = useChatSurface();

  const extractStrategy = useCapabilityStore((s) => s.strategyName('document_intelligence', 'extract'));
  const extractMutation = useExtractFromMessage(extractStrategy!);

  // Scene-only export — the conversation thread (chat-surface context;
  // no direct query_key here). Before the early returns per Rules of
  // Hooks. Carries the painted message bodies as the user reads them.
  useCardExport(useMemo(() => {
    const msgs = (visibleMessages ?? []) as ChatMessage[];
    const has = !!(entityType && entityId);
    return {
      label: 'Messages',
      content_key: 'chat_thread',
      surface: 'chat' as const,
      status: (has ? (isLoading && !chatData ? 'loading' : 'loaded') : 'idle') as
        | 'loading' | 'loaded' | 'idle',
      query_key: null,
      text: has ? `${msgs.length} messages${isTender ? ' (tender)' : ''}` : 'No selection',
      json: has
        ? {
            entity_type: entityType,
            entity_id: entityId,
            is_tender: !!isTender,
            active_sibling: activeSibling ?? null,
            message_count: allMessages?.length ?? msgs.length,
            messages: msgs.map((m) => ({
              author: m.author_name,
              platform: m.platform,
              body: m.body_translated || m.body,
              at: m.created_date,
              attachments: m.attachment_count,
            })),
          }
        : null,
    };
  }, [entityType, entityId, visibleMessages, allMessages, isTender, activeSibling, isLoading, chatData]));

  if (!entityType || !entityId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
        <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Select an item to see messages</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-8 w-8 mb-2 animate-spin" />
        <p className="text-sm">Loading messages...</p>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-sm text-destructive">{String(error)}</div>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Messages</span>
          {chatData?.entity_name && (
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              — {chatData.entity_name}
            </span>
          )}
          {isTender && chatData?.x_rfq_group_id && (
            <span className="text-[10px] text-muted-foreground font-mono truncate">
              {chatData.x_rfq_group_id}
            </span>
          )}
        </div>
        <Badge variant="secondary" className="text-[10px] px-1.5">
          {visibleMessages.length}
          {isTender && visibleMessages.length !== allMessages.length && (
            <span className="text-muted-foreground/70 ml-0.5">/{allMessages.length}</span>
          )}
        </Badge>
      </div>

      {isTender && (
        <div className="border-b border-border bg-muted/20 overflow-x-auto scrollbar-thin">
          <div className="flex gap-1 px-2 py-1.5 min-w-max">
            <SiblingTab
              active={activeSibling === ALL_SIBLINGS}
              label="All"
              count={allMessages.length}
              onClick={() => setActiveSibling(ALL_SIBLINGS)}
            />
            {siblings.map((s) => (
              <SiblingTab
                key={s.rfq_sid}
                active={activeSibling === s.rfq_sid}
                label={s.vendor_name || s.rfq_name || s.rfq_sid}
                count={countsBySibling.get(s.rfq_sid) ?? 0}
                onClick={() => setActiveSibling(s.rfq_sid)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto scrollbar-thin">
        {visibleMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground p-4">
            <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
          </div>
        ) : (
          visibleMessages.map((msg, idx) => {
            const initial = (msg.author_name || '?')[0].toUpperCase();
            const attachments = parseAttachments(msg.attachment_urls, msg.attachment_metadata);

            return (
              <div
                key={msg.cdk}
                className={cn(
                  'flex gap-3 px-3 py-3 hover:bg-accent/30 transition-colors',
                  idx < visibleMessages.length - 1 && 'border-b border-border/50',
                )}
              >
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[10px] font-semibold',
                  avatarColor(msg.author_name || ''),
                )}>
                  {initial}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium truncate">{msg.author_name}</span>
                    {msg.platform && <PlatformBadge platform={msg.platform} />}
                    {isTender && activeSibling === ALL_SIBLINGS && msg.vendor_name && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate">
                        {msg.vendor_name}
                      </span>
                    )}
                    {msg.created_date && (
                      <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
                        {formatTimestamp(msg.created_date)}
                      </span>
                    )}
                  </div>
                  <TranslatedBody msg={msg} />
                  {attachments.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {attachments.map((att, i) => {
                        const Icon = getFileIcon(att.mimetype);
                        const eligible = DI_ELIGIBLE_MIMETYPES.has(att.mimetype);
                        const extractingThis = extractMutation.isPending
                          && extractMutation.variables?.message_cdk === msg.cdk
                          && (extractMutation.variables?.attachment_index ?? 0) === i;
                        return (
                          <div key={i} className="relative group">
                            <a
                              href={att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-[10px] text-primary hover:underline bg-primary/5 border border-primary/20 rounded-lg px-2 py-1 transition-colors hover:bg-primary/10"
                            >
                              <Icon className="h-3 w-3 shrink-0" />
                              <span className="truncate max-w-[120px]">{att.filename}</span>
                              {att.size > 0 && (
                                <span className="text-muted-foreground shrink-0">{formatSize(att.size)}</span>
                              )}
                            </a>
                            {eligible && (
                              <button
                                title="Extract with Document Intelligence"
                                disabled={extractMutation.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  extractMutation.mutate(
                                    { message_cdk: msg.cdk, attachment_index: i },
                                    {
                                      onSuccess: (res) => {
                                        if (res.success) {
                                          toast.success(
                                            `Extracted: ${res.matched_line_count} matched, ${res.unmatched_line_count} unmatched`,
                                          );
                                        } else {
                                          toast.error(res.error || 'Extraction failed');
                                        }
                                      },
                                      onError: (err) => {
                                        toast.error(err.message || 'Extraction failed');
                                      },
                                    },
                                  );
                                }}
                                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-primary/90 disabled:opacity-50"
                              >
                                {extractingThis
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <ScanLine className="h-3 w-3" />
                                }
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
